/**
 * ADMIN BROADCAST PAGE
 * 
 * Send notifications to all leBusiness users
 * Owner/Admin only
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, Megaphone, Users, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/admin/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    // Use profile (transformed User from database) instead of user (Supabase auth user)
    // context.auth.user is the Supabase auth user (doesn't have isSuperAdmin)
    // context.auth.profile is the database User with transformed camelCase fields
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminBroadcastPage,
});

function AdminBroadcastPage() {
  const { toast } = useToast();
  
  const [broadcast, setBroadcast] = useState({
    title: '',
    message: '',
    type: 'system' as 'system' | 'alert' | 'info',
    priority: 'normal' as 'normal' | 'high' | 'urgent',
    send_in_app: true,
    send_email: false,
    send_sms: false,
    action_url: '',
    action_label: '',
  });

  const handleSendBroadcast = async () => {
    if (!broadcast.title || !broadcast.message) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Le titre et le message sont obligatoires',
      });
      return;
    }

    try {
      // TODO: Implement actual broadcast function
      // This would query all users and send notification to each
      
      // Placeholder for now:
      console.log('📢 Broadcasting to all users:', broadcast);
      
      // Example implementation:
      /*
      const { data: users } = await supabase
        .from('users')
        .select('id');
      
      for (const user of users) {
        await NotificationService.create({
          user_id: user.id,
          ...broadcast,
        });
      }
      */

      toast({
        title: '📢 Broadcast envoyé!',
        description: `Notification envoyée à tous les utilisateurs de leBusiness`,
      });

      // Reset form
      setBroadcast({
        title: '',
        message: '',
        type: 'system',
        priority: 'normal',
        send_in_app: true,
        send_email: false,
        send_sms: false,
        action_url: '',
        action_label: '',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible d\'envoyer le broadcast',
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Megaphone className="h-8 w-8" />
            Admin Broadcast
          </h1>
          <p className="text-muted-foreground mt-2">
            Envoyer des notifications à tous les utilisateurs de leBusiness
          </p>
        </div>

        {/* Warning */}
        <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900 dark:text-orange-100">
                  ⚠️ Zone Admin
                </h3>
                <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                  Les broadcasts sont envoyés à <strong>TOUS</strong> les utilisateurs de leBusiness.
                  Utilisez cette fonctionnalité avec précaution.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Broadcast Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Créer un Broadcast
            </CardTitle>
            <CardDescription>
              Composez votre message pour tous les utilisateurs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Titre du Broadcast
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Ex: Nouvelle version disponible!"
                value={broadcast.title}
                onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">
                Message
                <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="message"
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder="Ex: leBusiness v2.0 est disponible avec de nouvelles fonctionnalités..."
                value={broadcast.message}
                onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })}
              />
            </div>

            {/* Type & Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  value={broadcast.type}
                  onChange={(e) => setBroadcast({ ...broadcast, type: e.target.value as any })}
                >
                  <option value="system">⚙️ Système</option>
                  <option value="info">ℹ️ Information</option>
                  <option value="alert">⚠️ Alerte</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priorité</Label>
                <select
                  id="priority"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  value={broadcast.priority}
                  onChange={(e) => setBroadcast({ ...broadcast, priority: e.target.value as any })}
                >
                  <option value="normal">Normal</option>
                  <option value="high">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Action (Optional) */}
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <h4 className="font-semibold">Action (Optionnel)</h4>
              
              <div className="space-y-2">
                <Label htmlFor="action_url">URL de l'action</Label>
                <Input
                  id="action_url"
                  placeholder="/apps/pos"
                  value={broadcast.action_url}
                  onChange={(e) => setBroadcast({ ...broadcast, action_url: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="action_label">Libellé du bouton</Label>
                <Input
                  id="action_label"
                  placeholder="Découvrir"
                  value={broadcast.action_label}
                  onChange={(e) => setBroadcast({ ...broadcast, action_label: e.target.value })}
                />
              </div>
            </div>

            {/* Channels */}
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <h4 className="font-semibold">Canaux de diffusion</h4>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={broadcast.send_in_app}
                    onChange={(e) => setBroadcast({ ...broadcast, send_in_app: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>📱 In-App (Cloche de notification)</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={broadcast.send_email}
                    onChange={(e) => setBroadcast({ ...broadcast, send_email: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>📧 Email (nécessite configuration SMTP)</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer opacity-50">
                  <input
                    type="checkbox"
                    checked={broadcast.send_sms}
                    onChange={(e) => setBroadcast({ ...broadcast, send_sms: e.target.checked })}
                    className="w-4 h-4"
                    disabled
                  />
                  <span>📱 SMS (bientôt disponible)</span>
                </label>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-4 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
              <h4 className="font-semibold">📱 Aperçu</h4>
              <div className="p-3 bg-white dark:bg-gray-900 rounded-lg border">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">
                    {broadcast.type === 'system' ? '⚙️' : broadcast.type === 'alert' ? '⚠️' : 'ℹ️'}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold">
                      {broadcast.title || 'Titre du broadcast'}
                    </h5>
                    <p className="text-sm text-muted-foreground mt-1">
                      {broadcast.message || 'Message du broadcast...'}
                    </p>
                    {broadcast.action_label && (
                      <Button size="sm" className="mt-2">
                        {broadcast.action_label}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Send Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSendBroadcast}
                size="lg"
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                📢 Envoyer à tous les utilisateurs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Templates */}
        <Card>
          <CardHeader>
            <CardTitle>Modèles rapides</CardTitle>
            <CardDescription>Exemples de broadcasts courants</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start text-left h-auto p-4"
              onClick={() => setBroadcast({
                ...broadcast,
                title: '🎉 Nouvelle version disponible!',
                message: 'leBusiness v2.0 est maintenant disponible avec de nouvelles fonctionnalités pour améliorer votre gestion.',
                type: 'system',
                priority: 'normal',
                action_url: '/apps',
                action_label: 'Découvrir',
              })}
            >
              <div>
                <div className="font-semibold">🎉 Nouvelle version</div>
                <div className="text-sm text-muted-foreground">Annonce de mise à jour</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start text-left h-auto p-4"
              onClick={() => setBroadcast({
                ...broadcast,
                title: '🛠️ Maintenance planifiée',
                message: 'Une maintenance est prévue le 15 janvier à 2h00. Le service sera indisponible pendant 1 heure.',
                type: 'alert',
                priority: 'high',
              })}
            >
              <div>
                <div className="font-semibold">🛠️ Maintenance</div>
                <div className="text-sm text-muted-foreground">Avis de maintenance</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start text-left h-auto p-4"
              onClick={() => setBroadcast({
                ...broadcast,
                title: '📋 Nouvelles obligations fiscales',
                message: 'Attention: De nouvelles obligations fiscales entrent en vigueur ce mois-ci. Vérifiez votre conformité.',
                type: 'alert',
                priority: 'urgent',
                action_url: '/apps/compliance',
                action_label: 'Vérifier',
              })}
            >
              <div>
                <div className="font-semibold">📋 Obligation fiscale</div>
                <div className="text-sm text-muted-foreground">Alerte de conformité</div>
              </div>
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default AdminBroadcastPage;
